import { Product } from "@~/types/types";
import { RefObject } from "react";

export type ProductListRef = RefObject<HTMLElement | null>;

export type ProductListEventDetail = {
  pim: string;
  variantId: string;
  skuId: string;
  instanceId: string | undefined;
  index: number;
  quantity: number | undefined;
  product: {
    currency: string;
    hasSpecialOffer: boolean;
    noOfReviews: number;
    rating: string;
    priceHistory?: {
      chronology: number;
      display: {
        max: string;
        min: string;
      };
    }[];
  };
};

export type ProductListEvent = {
  type: string;
  product?: Product;
  index?: number;
  quantity?: number;
  instanceId?: string;
  variantId?: string;
  skuId?: string;
};

type ProductListEventDispatcher = (event: ProductListEvent) => void;

export function dispatchCustomEvent(
  ref: ProductListRef,
  type: string,
  detail: Record<string, unknown> = {},
) {
  const event = new CustomEvent(type, {
    detail,
    bubbles: true,
    cancelable: true,
    composed: true,
  });
  ref.current?.dispatchEvent(event);
}

export function createProductListEventDispatcher(
  ref: ProductListRef,
): ProductListEventDispatcher {
  return (event: ProductListEvent) => {
    dispatchProductListEvent(ref, event);
  };
}

export function dispatchProductListEvent(
  ref: ProductListRef,
  productListAnalyticsEvent: ProductListEvent,
) {
  const { product, variantId } = productListAnalyticsEvent;
  const detail =
    product && variantId
      ? createProductListEventDetail(productListAnalyticsEvent)
      : productListAnalyticsEvent;
  dispatchCustomEvent(ref, productListAnalyticsEvent.type, detail);
}

export function createProductListEventDetail(
  productListAnalyticsEvent: ProductListEvent,
): ProductListEventDetail {
  const { product, index, quantity, instanceId, variantId, skuId } =
    productListAnalyticsEvent;
  return {
    pim: product?.aliases.pimProductId || "",
    variantId: variantId || "",
    skuId: skuId || "",
    instanceId: instanceId,
    index: index || 0,
    quantity: quantity,
    product: {
      currency: "GBP",
      hasSpecialOffer: !!product && hasSpecialOffer(product),
      noOfReviews: product?.reviewSummary?.numberOfReviews || 0,
      rating: product?.reviewSummary?.averageRating || "",
      priceHistory: product?.variantPriceRange?.reductionHistory,
    },
  };
}

function hasSpecialOffer(product: Product): boolean {
  const { messaging } = product;
  return messaging
    ? messaging?.filter((m) => m.type === "promotional").length > 0
    : false;
}
